import imageCompression from 'browser-image-compression';

export interface CompressionOptions {
  maxSizeMB?: number;
  maxWidthOrHeight?: number;
  useWebWorker?: boolean;
  quality?: number;
}

/**
 * Compress an image file to reduce its size
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Compressed file as base64 data URL
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<string> {
  try {
    // Default compression options: JPEG, max 800px, 70% quality, max 500KB
    const compressionOptions = {
      maxSizeMB: options.maxSizeMB ?? 0.5, // 500KB
      maxWidthOrHeight: options.maxWidthOrHeight ?? 800,
      useWebWorker: options.useWebWorker ?? true,
      fileType: 'image/jpeg' as const, // Convert to JPEG for smallest file size
      initialQuality: options.quality ?? 0.7, // 70% quality
    };

    // Compress the image
    const compressedFile = await imageCompression(file, compressionOptions);

    // Convert compressed file to base64 data URL
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = (error) => {
        reject(new Error('Failed to convert image to base64: ' + error));
      };
      reader.readAsDataURL(compressedFile);
    });
  } catch (error) {
    throw new Error(`Image compression failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Validate image file before compression
 * @param file - The image file to validate
 * @returns Validation result with error message if invalid
 */
export function validateImageFile(file: File): { valid: boolean; error?: string } {
  // Check file type
  if (!file.type.startsWith('image/')) {
    return { valid: false, error: 'Please select a valid image file' };
  }

  // Check file size (before compression, allow up to 10MB)
  const maxSizeMB = 10;
  const fileSizeMB = file.size / (1024 * 1024);
  if (fileSizeMB > maxSizeMB) {
    return {
      valid: false,
      error: `Image size (${fileSizeMB.toFixed(1)}MB) exceeds maximum allowed size (${maxSizeMB}MB). Please select a smaller image.`,
    };
  }

  return { valid: true };
}

